import {
  CalendarIcon,
  ChatAlt2Icon,
  CodeIcon,
  CursorClickIcon,
  DocumentIcon,
  ExclamationCircleIcon,
  GiftIcon,
  GlobeAltIcon,
  IdentificationIcon,
  LightningBoltIcon,
  RefreshIcon,
  TagIcon,
} from '@heroicons/react/outline';
import { FaWebflow } from 'react-icons/fa6';

import { useWebsiteFaqs } from './websiteShared';
import CodePanel from '../mocks/CodePanel';
import SiteChatDemo, { ChatButton } from '../mocks/SiteChatDemo';
import WidgetApi from '../mocks/WidgetApi';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Orbit',
  initial: 'O',
  bg: 'bg-[#0891b2]',
};

/*
 * Facts this page relies on (checked 2026-10-21,
 * docs/agents/website-platforms.mdx#webflow, docs/developers/chat-widget.mdx):
 * the snippet goes into Site settings → Custom code → Footer code and goes
 * live on publish; custom code is for paid Webflow site plans; one page uses
 * its Before </body> setting or an Embed element; window.easyPeasyChat has
 * open/close/toggle, hide/show, setContact and events, queues calls made
 * before load, and setContact labels (doesn't verify) the visitor.
 */
export function useWebflowContent(): ChannelContent {
  const { t } = useChannelsT();
  const websiteFaqs = useWebsiteFaqs();

  return {
    key: 'webflow',
    name: 'Webflow',
    logo: 'FaWebflow',
    seoTitle: t('Webflow AI Chatbot: Add an AI Agent to Your Webflow Site'),
    seoDescription: t(
      'Add an AI chatbot to Webflow with one snippet in Custom code. It answers visitors from your pages and docs, and a JavaScript API puts you in control.',
    ),
    hero: {
      label: t('Webflow AI chatbot'),
      title: t('Answers for every visitor to your Webflow site.'),
      accent: t('With full control in code.'),
      lead: t(
        'Paste one snippet into your site’s custom code and publish. A chat bubble trained on your pages and docs answers visitors, and a small JavaScript API lets you open it from your own buttons.',
      ),
      facts: [
        t('Works on the Free plan'),
        t('One snippet in Footer code'),
        t('JavaScript API'),
      ],
      description: t(
        'Example: a visitor on a software company’s Webflow site asks whether they can import data from a spreadsheet. The agent answers from the docs and shows a Read the import guide button.',
      ),
      demo: (
        <SiteChatDemo
          brand={BRAND}
          url='orbit-analytics.webflow.io'
          site={{
            headline: t('Know your numbers in real time.'),
            picture:
              'bg-[linear-gradient(135deg,#67e8f9_0%,#0891b2_55%,#164e63_100%)]',
            accent: 'text-[#0891b2] dark:text-[#67e8f9]',
          }}
          lines={[
            {
              from: 'visitor',
              text: t('Can I import my data from a spreadsheet?'),
              at: 700,
            },
            {
              from: 'agent',
              content: t(
                'Yes! Upload a CSV file and Orbit matches the columns for you in a few clicks.',
              ),
              typingAt: 1200,
              at: 2100,
            },
            {
              from: 'agent',
              content: (
                <ChatButton brand={BRAND} label={t('Read the import guide')} />
              ),
              at: 2700,
            },
          ]}
        />
      ),
      wide: true,
      steps: [],
    },
    jobs: {
      title: t('Your docs, as a conversation.'),
      tail: t('On every page of your site.'),
      lead: t(
        'Visitors want answers, not a search through your docs. Your agent knows your pages, pricing and help center, and answers in seconds.',
      ),
      items: [
        {
          icon: ChatAlt2Icon,
          title: t('Answers questions'),
          detail: t(
            'Features, pricing, setup and policies, straight from your pages and docs.',
          ),
        },
        {
          icon: CursorClickIcon,
          title: t('Shows buttons'),
          detail: t(
            'Sends visitors to booking, pricing or checkout pages with a button in the chat.',
          ),
        },
        {
          icon: IdentificationIcon,
          title: t('Captures leads'),
          detail: t(
            'Collects names, emails and anything else you ask for, one question at a time, and saves them to Leads.',
          ),
        },
        {
          icon: CalendarIcon,
          title: t('Books demos'),
          detail: t(
            'Checks when you’re free and books the time in your Google Calendar.',
          ),
        },
        {
          icon: CodeIcon,
          title: t('Fits your build'),
          detail: t(
            'Open, hide and show the chat from your own code, and tell it who is signed in.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
      ],
    },
    setup: {
      title: t('Paste it, then publish.'),
      tail: t('One snippet, every page.'),
      steps: [
        {
          title: t('Copy the snippet'),
          detail: t(
            'In Easy-Peasy.AI, open your agent, go to Channels, then Chat bubble, click Manage and copy the install snippet.',
          ),
        },
        {
          title: t('Paste it into Footer code'),
          detail: t(
            'In Webflow, open Site settings, then the Custom code tab, paste the snippet into Footer code and click Save changes.',
          ),
        },
        {
          title: t('Publish'),
          detail: t(
            'Custom code goes live when you publish. Click Publish and open your live site.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/website-platforms#webflow',
      guideLabel: t('Read the Webflow setup guide'),
      description: t(
        'Webflow’s custom code settings with the install snippet in Footer code, and a note that it goes live when you publish.',
      ),
      visual: (
        <CodePanel
          platform={
            <>
              <FaWebflow className='h-5 w-5 text-[#146EF5]' />
              <span className='text-sm font-semibold'>Webflow</span>
            </>
          }
          title={t('Custom code')}
          fields={[]}
          codeLabel={t('Footer code')}
          primary={t('Save changes')}
          primaryClass='bg-[#146ef5] text-white'
          note={t('Goes live when you publish')}
        />
      ),
    },
    spotlight: {
      title: t('Control the chat from your code.'),
      tail: t('A few lines, no plugin.'),
      lead: t(
        'Open the chat from your own buttons, hide it on checkout pages and tell the agent who is signed in. Calls made before the chat has loaded wait and run once it’s ready.',
      ),
      points: [
        t('open(), close() and toggle() from any button'),
        t('hide() and show() as visitors move between pages'),
        t('setContact() labels conversations with the visitor'),
        t('Conversations show the page each chat started on'),
      ],
      description: t(
        'Code that opens the chat from a button and passes the signed-in visitor’s name and email, next to a conversation labelled with that visitor and the page where it started.',
      ),
      visual: <WidgetApi page='/pricing' />,
    },
    rules: {
      title: t('Good to know.'),
      tail: t('Webflow, in plain words.'),
      items: [
        {
          icon: GiftIcon,
          title: t('Free plan included'),
          detail: t(
            'The website chat works on every plan, including Free. Actions, such as buttons and bookings, need a paid plan.',
          ),
        },
        {
          icon: ExclamationCircleIcon,
          title: t('Webflow plans'),
          detail: t(
            'Custom code works on paid Webflow site plans, and goes live when you publish.',
          ),
        },
        {
          icon: DocumentIcon,
          title: t('One page only'),
          detail: t(
            'To show the chat on one page, paste the snippet into that page’s Before </body> tag setting instead.',
          ),
        },
        {
          icon: LightningBoltIcon,
          title: t('Doesn’t slow your site'),
          detail: t(
            'The bubble shows right away, and the chat itself loads once the page has settled.',
          ),
        },
        {
          icon: RefreshIcon,
          title: t('Changes go live on their own'),
          detail: t(
            'Change the bubble’s look or greeting in Appearance and your site updates within about a minute, with no new code.',
          ),
        },
        {
          icon: TagIcon,
          title: t('Labels, not logins'),
          detail: t(
            'Details passed with setContact() label conversations. They aren’t verified, so don’t use them to decide what the agent may share.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('How do I add an AI chatbot to my Webflow site?'),
        answer: t(
          'Copy your agent’s install snippet, open Site settings in Webflow, paste it into Footer code on the Custom code tab, save and publish. The chat bubble appears on every page.',
        ),
      },
      {
        question: t('Can I open the chat from my own button?'),
        answer: t(
          'Yes. Call window.easyPeasyChat.open() from any button. You can also hide and show the chat, and tell the agent who is signed in with setContact().',
        ),
      },
      ...websiteFaqs,
    ],
    finalTitle: t('Your Webflow site could be answering by lunchtime.'),
    finalExchange: {
      question: t('Is there a free trial?'),
      answer: t('Yes, 14 days with every feature, and no card needed.'),
    },
  };
}
